import * as React from 'react';

/**
 * Reads Mora's active theme directly off `<html>` rather than via
 * `useTheme()`.
 *
 * `ThemeProvider` sets `.dark`/`.light` on `document.documentElement`, and
 * this mirrors that signal instead of requiring the component to render
 * inside a `ThemeProvider` ancestor. That independence is load-bearing for
 * this directory specifically: a preview replica renders in the style guide,
 * in unit tests, and inside embedding surfaces that may not mount the full
 * app provider tree — and `useTheme()` THROWS outside one.
 *
 * This lived as three byte-identical private copies (`MetaAdPreview`,
 * `InstagramAdPreview`, `LinkedInAdPreview`). The duplication was deliberate
 * at the time and said so in a comment — the files were owned by different
 * concurrent lanes and importing across them would have raced. That is over;
 * all of them are one program now, and a fourth copy for
 * `GoogleRsaAdPreview` would have been indefensible under the repo's
 * search-by-the-noun-before-you-write-it rule.
 */
export function useResolvedDarkMode(): boolean {
  const [isDark, setIsDark] = React.useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
  );

  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const sync = () => setIsDark(root.classList.contains('dark'));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}
